import {
  Box3,
  MOUSE,
  PerspectiveCamera,
  Sphere,
  Spherical,
  Vector3,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

/**
 * The closest the camera may come to its orbit target, in subject radii.
 * Low enough to work on an eyelid with the head as the subject: it was 0.4
 * after a frame, which stopped a pinch dead about a quarter of a head from
 * the last stroke (owner report), and 0.1 of whatever distance a restored
 * camera or a lens change happened to leave.
 */
const MIN_DISTANCE = 0.02;
/**
 * The farthest, in subject radii - or twice the framing distance, which a
 * long lens needs (at 135mm a frame alone sits beyond ten radii).
 */
const MAX_DISTANCE = 10;
const MAX_OF_FIT = 2;
/**
 * The near plane follows the camera in: never more than this fraction of
 * the target distance, so a surface the camera has come right up to is
 * not clipped away, and never more than NEAR_OF_RADIUS, which is what it
 * always was at framing distances, where depth precision wants it large.
 */
const NEAR_OF_DISTANCE = 1 / 50;
const NEAR_OF_RADIUS = 1 / 100;
const FAR_OF_RADIUS = 100;

/**
 * Damping, as the share of the motion still owed that one 60 Hz frame
 * applies. It used to be a flat 0.08 per frame, so the feel followed the
 * frame rate: half of a drag was on screen after eight frames, which is
 * 130 ms at 60 fps and twice that at 30. Both numbers are now per 60 Hz
 * frame and scaled by the time each frame took (dampingFor), so any frame
 * rate feels the way 60 always did.
 *
 * While a pointer holds the view it follows closely (HELD: half a move
 * shows within two frames); once it lets go, the old coast takes over
 * (COAST), started at the speed the view was moving so a flick carries on
 * about as far as it used to (see release).
 */
const DAMPING_HELD = 0.3;
const DAMPING_COAST = 0.08;
/** How quickly the tracked release speed follows the view, in seconds. */
const VELOCITY_TAU = 0.05;
/** OrbitControls' state with no pointer holding the view. */
const STATE_NONE = -1;

/** A place on the page, in client px. */
export interface ScreenPoint {
  x: number;
  y: number;
}

/**
 * Where a zoom heads, asked at the moment it happens: `at` is where the
 * gesture is on the page (the wheel's pointer, the pinch's centre, the
 * press that began a drag zoom), or null when no place is known. Null back
 * means "nowhere in particular": the zoom heads for the orbit target.
 */
export type ZoomFocus = (at: ScreenPoint | null) => Vector3 | null;

/** What OrbitControls keeps of the pointers holding it (three r184). */
interface OrbitPointers {
  _pointers: number[];
  _pointerPositions: Record<number, unknown>;
  _onPointerMove: (e: PointerEvent) => void;
  _onPointerUp: (e: PointerEvent) => void;
}

/** The `buttons` bit of a `button` number (0 left 1, 1 middle 4, 2 right 2, then back/forward). */
export function buttonMask(button: number): number {
  if (button === 1) return 4;
  if (button === 2) return 2;
  return button >= 0 && button < 16 ? 1 << button : 0;
}

/** A per-60 Hz-frame damping share, for a frame that took `dt` seconds. */
export function dampingFor(perFrame60: number, dt: number): number {
  return 1 - Math.pow(1 - perFrame60, Math.max(0, dt) * 60);
}

/** What the damping reads and writes inside OrbitControls. */
interface OrbitInternals {
  state: number;
  dampingFactor: number;
  _sphericalDelta: Spherical;
  _panOffset: Vector3;
  _scale: number;
}

/**
 * DCC-style camera navigation (design doc §7).
 *
 * OrbitControls with a remapped button scheme and damping. The fixed up-vector
 * (no roll) gives stable framing for a bust or figure. The target is kept
 * stable across frame swaps so changing geometry never jumps the camera.
 *
 *   Left   drag → orbit
 *   Middle drag → pan
 *   Right  drag → dolly (zoom)
 *   Wheel       → dolly (zoom)
 *
 * Zooms run along the view axis to the target unless a zoom focus is set
 * (Sculpt mode sets one): then they head for the point it gives.
 */
export class Controls {
  readonly controls: OrbitControls;

  /** Default viewing direction (camera offset from target), normalised. */
  private readonly viewDir = new Vector3(0.9, 0.55, 1).normalize();
  /**
   * The subject's radius, which the dolly limits and the clip planes scale
   * with; null until a subject is known, when the camera is left unbounded.
   */
  private radius: number | null = null;
  /**
   * The least the far plane may be: how far a fixed floor's disc reaches
   * from the camera (Viewer.layoutHeldFloor), so a small subject's tight
   * clip planes do not cut the floor it stands on short. 0 with no floor
   * of that kind.
   */
  private farFloor = 0;
  /**
   * Where zooms head (see setZoomFocus); null leaves every zoom to
   * OrbitControls, along the view axis to the target.
   */
  private zoomFocus: ZoomFocus | null = null;
  /**
   * Told after a zoom has scaled camera and target about `point` by `s`
   * (Sculpt's pivot orbit moves its record of the view the same way, so
   * it does not read the zoom as part of an orbit and apply it twice).
   */
  onFocusZoom: ((point: Vector3, s: number) => void) | null = null;
  /** The fingers on the glass, by pointer id: a pinch zooms at their middle. */
  private readonly touches = new Map<number, ScreenPoint>();
  /** The last place a mouse or pen pointer, or the wheel, was seen. */
  private pointerAt: ScreenPoint | null = null;
  /** Where the mouse or pen drag OrbitControls is running began. */
  private dragAt: ScreenPoint | null = null;
  private readonly zoomScratch = new Vector3();

  constructor(
    private readonly camera: PerspectiveCamera,
    domElement: HTMLElement,
  ) {
    const controls = new OrbitControls(camera, domElement);
    controls.mouseButtons = {
      LEFT: MOUSE.ROTATE,
      MIDDLE: MOUSE.PAN,
      RIGHT: MOUSE.DOLLY,
    };
    controls.enableZoom = true; // scroll wheel
    controls.enableDamping = true; // smooth, non-snapping feel
    controls.dampingFactor = 0.08;
    controls.zoomSpeed = 0.9;
    this.controls = controls;
    this.domElement = domElement;
    // Every zoom OrbitControls makes (the wheel, a pinch, a right drag)
    // ends in its update(), which it also calls straight from its event
    // handlers: what it owes in _scale is taken here first and, with a
    // zoom focus set, applied about that point instead (zoomTowardFocus).
    const orbitUpdate = controls.update.bind(controls);
    controls.update = (deltaTime?: number | null): boolean => {
      this.zoomTowardFocus();
      return orbitUpdate(deltaTime);
    };
    // Before OrbitControls on the press (the window's capture phase runs
    // ahead of the canvas), after it to see what it started, and on the
    // window for every way a drag can end without its pointerup.
    window.addEventListener('pointerdown', this.onPressFirst, true);
    domElement.addEventListener('pointerdown', this.onPressAfter);
    window.addEventListener('pointermove', this.onAnyMove, true);
    window.addEventListener('pointerup', this.onAnyUp, true);
    window.addEventListener('pointercancel', this.onAnyCancel, true);
    domElement.addEventListener('lostpointercapture', this.onLostCapture);
    window.addEventListener('blur', this.onWindowBlur);
    window.addEventListener('wheel', this.onWheelFirst, { capture: true, passive: true });
  }

  private readonly domElement: HTMLElement;
  /**
   * The mouse or pen drag OrbitControls is running: its pointer and the
   * `buttons` bit of the button that began it. A pen's barrel button can
   * let go without a pointerup (Windows 11 Ink: the release arrives as the
   * hover's pointermove, buttons 0, or as an up from another pointer), and
   * OrbitControls, which never reads `buttons` on a move, then held the
   * view for good - every hover move turned it, with no way out short of
   * leaving the page. So the drag ends here as soon as the button that
   * began it is no longer down, whatever does or does not arrive.
   */
  private drag: { pointerId: number; mask: number } | null = null;
  /** The button map to put back after a pen barrel press borrowed it. */
  private savedButtons: OrbitControls['mouseButtons'] | null = null;

  /**
   * A pen's barrel button pans (owner call), whichever number its driver
   * reports it as: middle (button 1, buttons 4) or right (button 2,
   * buttons 2), where a right press would otherwise dolly. OrbitControls
   * reads the map once, on the press; it goes back after.
   */
  private readonly onPressFirst = (e: PointerEvent): void => {
    this.restoreButtons();
    this.notePointer(e);
    if (e.pointerType !== 'pen' || (e.button !== 1 && e.button !== 2)) return;
    if (e.target !== this.domElement) return;
    this.savedButtons = { ...this.controls.mouseButtons };
    this.controls.mouseButtons = { LEFT: this.savedButtons.LEFT, MIDDLE: MOUSE.PAN, RIGHT: MOUSE.PAN };
  };

  private readonly onPressAfter = (e: PointerEvent): void => {
    this.restoreButtons();
    if (e.pointerType === 'touch') return;
    const c = this.controls as unknown as OrbitInternals & OrbitPointers;
    // Only a press OrbitControls took (the sculpt shell keeps a stroke's).
    if (c.state === STATE_NONE || !c._pointers.includes(e.pointerId)) return;
    this.drag = { pointerId: e.pointerId, mask: buttonMask(e.button) };
    this.dragAt = { x: e.clientX, y: e.clientY };
  };

  /** Where the pointers are, for where a zoom heads (zoomAt). */
  private notePointer(e: PointerEvent): void {
    if (e.pointerType === 'touch') this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
    else this.pointerAt = { x: e.clientX, y: e.clientY };
  }

  private readonly onWheelFirst = (e: WheelEvent): void => {
    this.pointerAt = { x: e.clientX, y: e.clientY };
  };

  private restoreButtons(): void {
    if (!this.savedButtons) return;
    this.controls.mouseButtons = this.savedButtons;
    this.savedButtons = null;
  }

  /**
   * A move from a mouse or pen whose buttons no longer include the one
   * that began the drag ends it (a hovering pen reports buttons 0). Not a
   * finger's: touch navigation is OrbitControls' own and ends on its lifts.
   * Nothing re-enters another mode when the button set changes mid-drag:
   * a pan stays a pan until its button goes, and then the drag is over.
   */
  private readonly onAnyMove = (e: PointerEvent): void => {
    this.notePointer(e);
    const d = this.drag;
    if (!d || e.pointerType === 'touch') return;
    // Only the drag's own pointer: Firefox on Windows interleaves mouse
    // moves (buttons 0) with a Wacom pen's, and one of those must not end
    // a pen drag on its first move.
    if (e.pointerId !== d.pointerId) return;
    if ((e.buttons & d.mask) === 0) this.endDrag();
  };

  /**
   * The drag's own pointerup is OrbitControls' to handle. An up from some
   * other mouse or pen pointer (the barrel's release reported under
   * another id) that leaves the drag's button up ends it here: let
   * through, OrbitControls would drop no pointer and fall back to a
   * one-finger rotate on the one it still holds.
   */
  private readonly onAnyUp = (e: PointerEvent): void => {
    this.touches.delete(e.pointerId);
    const d = this.drag;
    if (!d) return;
    if (e.pointerId === d.pointerId) {
      this.drag = null;
      return;
    }
    if (e.pointerType !== 'touch' && (e.buttons & d.mask) === 0) this.endDrag();
  };

  private readonly onAnyCancel = (e: PointerEvent): void => {
    this.touches.delete(e.pointerId);
    if (this.drag && e.pointerId === this.drag.pointerId) this.endDrag();
  };

  /**
   * A lost capture ends the drag only once its button is up. Firefox and
   * Zen on Windows take a pen's capture back a few frames into a drag with
   * the tip still down (the owner's log: lostpointercapture pen b1, 80 ms
   * after the press); OrbitControls follows the moves on the document
   * anyway, so the orbit carries on.
   */
  private readonly onLostCapture = (e: PointerEvent): void => {
    const d = this.drag;
    if (!d || e.pointerId !== d.pointerId) return;
    if ((e.buttons & d.mask) === 0) this.endDrag();
  };

  private readonly onWindowBlur = (): void => {
    this.touches.clear();
    if (this.drag) this.endDrag();
  };

  /**
   * End the mouse or pen drag as OrbitControls' own pointerup would: its
   * pointers dropped, its document listeners off, the capture let go, its
   * state none. Done by hand rather than through its handler, which
   * releases the capture before anything else and throws there for a
   * pointer the browser no longer has.
   */
  private endDrag(): void {
    this.drag = null;
    const c = this.controls as unknown as OrbitInternals & OrbitPointers;
    for (const id of c._pointers) {
      try {
        if (this.domElement.hasPointerCapture(id)) this.domElement.releasePointerCapture(id);
      } catch {
        // A pointer the browser has already forgotten holds no capture.
      }
    }
    c._pointers.length = 0;
    c._pointerPositions = {};
    const doc = this.domElement.ownerDocument;
    doc.removeEventListener('pointermove', c._onPointerMove);
    doc.removeEventListener('pointerup', c._onPointerUp);
    if (c.state !== STATE_NONE) {
      c.state = STATE_NONE;
      this.controls.dispatchEvent({ type: 'end' });
    }
  }

  /** Whether a mouse or pen navigation drag is under way (the tests read it). */
  isDragging(): boolean {
    return this.drag !== null;
  }

  /** Frame `box` from the default viewing direction (initial load). */
  frameSubject(box: Box3): void {
    this.place(box, this.viewDir);
  }

  /**
   * Frame `box` keeping the current view direction — a DCC-style "frame
   * selected" (hotkey "f"): pan + dolly so the subject fills the viewport
   * without changing the orbit angle.
   */
  focus(box: Box3): void {
    const dir = new Vector3().subVectors(this.camera.position, this.controls.target);
    if (dir.lengthSq() < 1e-8) dir.copy(this.viewDir);
    this.place(box, dir.normalize());
  }

  private place(box: Box3, dir: Vector3): void {
    const sphere = box.getBoundingSphere(new Sphere());

    const r = Math.max(sphere.radius, 1e-4);
    this.radius = r;
    // A frame is a reset: an orbit still coasting would carry the view
    // straight off the framing it was asked for.
    this.halt();
    this.controls.target.copy(sphere.center);
    this.camera.position.copy(sphere.center).addScaledVector(dir, this.fitDistance(r));
    this.syncLimits();
    this.controls.update();
  }

  /**
   * How far back a sphere of radius r fits the view: into the vertical FOV,
   * then accounting for aspect so wide-but-short subjects still fit
   * horizontally, with a small margin.
   */
  private fitDistance(r: number): number {
    const vFov = (this.camera.fov * Math.PI) / 180;
    const fitHeight = r / Math.sin(vFov / 2);
    const fitWidth = fitHeight / Math.min(1, this.camera.aspect);
    return Math.max(fitHeight, fitWidth) * 1.15;
  }

  /**
   * The subject the limits scale with (every fit of the subject's bounds
   * sets it, framing or not). Set before a camera is restored, or the
   * restore is clamped to the old subject's limits.
   */
  setSubjectRadius(r: number): void {
    this.radius = Math.max(r, 1e-4);
    this.syncLimits();
  }

  /**
   * Re-derive the dolly limits from the subject and the lens, and the clip
   * planes from where the camera now is. Every route that moves the camera
   * (the wheel and the pinch inside OrbitControls, dollyBy, a fit, a
   * restore, a lens change) is clamped to the same floor and ceiling; the
   * viewer calls this once a frame, after everything has moved the camera
   * and before it renders, so the near plane always follows the distance.
   */
  syncLimits(): void {
    const r = this.radius;
    if (r === null) return;
    const c = this.controls;
    c.minDistance = r * MIN_DISTANCE;
    c.maxDistance = Math.max(r * MAX_DISTANCE, this.fitDistance(r) * MAX_OF_FIT);
    const dist = this.camera.position.distanceTo(c.target);
    const near = Math.max(Math.min(r * NEAR_OF_RADIUS, dist * NEAR_OF_DISTANCE), 1e-6);
    const far = Math.max(r * FAR_OF_RADIUS, c.maxDistance + 2 * r, this.farFloor);
    if (near !== this.camera.near || far !== this.camera.far) {
      this.camera.near = near;
      this.camera.far = far;
      this.camera.updateProjectionMatrix();
    }
  }

  /** The far plane's floor (see farFloor); the next syncLimits applies it. */
  setFarFloor(d: number): void {
    this.farFloor = Math.max(0, d);
  }

  /** A pointer held the view at the last update (a drag, or fingers on the glass). */
  private held = false;
  /** The view's motion per 60 Hz frame while held: what the coast starts from. */
  private readonly velocity = { theta: 0, phi: 0, pan: new Vector3() };
  private readonly panStep = new Vector3();

  /** Whether a pointer is holding the view (an orbit, pan or pinch under way). */
  isHeld(): boolean {
    return (this.controls as unknown as OrbitInternals).state !== STATE_NONE;
  }

  /** Advance the controls by one frame that took `dt` seconds. */
  update(dt = 1 / 60): void {
    const c = this.controls as unknown as OrbitInternals;
    const held = c.state !== STATE_NONE;
    if (this.held && !held) this.release(c);
    this.held = held;
    const step = Math.min(Math.max(dt, 0), 0.1);
    c.dampingFactor = dampingFor(held ? DAMPING_HELD : DAMPING_COAST, step);
    if (held && step > 0) this.trackVelocity(c, step);
    this.controls.update();
  }

  /**
   * What this frame applies, per 60 Hz frame, smoothed over VELOCITY_TAU:
   * read before the update, from what OrbitControls is owed and the share
   * it is about to apply. A pointer held still lets it run down to nothing,
   * as it should: a drag that stopped before letting go does not coast.
   */
  private trackVelocity(c: OrbitInternals, dt: number): void {
    const k = c.dampingFactor / (dt * 60);
    const a = 1 - Math.exp(-dt / VELOCITY_TAU);
    const v = this.velocity;
    v.theta += (c._sphericalDelta.theta * k - v.theta) * a;
    v.phi += (c._sphericalDelta.phi * k - v.phi) * a;
    this.panStep.copy(c._panOffset).multiplyScalar(k).sub(v.pan).multiplyScalar(a);
    v.pan.add(this.panStep);
  }

  /**
   * The pointer let go: owe the view what a coast from its current speed
   * travels, which is speed x (1 - COAST) / COAST - what the steady 0.08
   * damping always left owed at that speed. Whatever is owed already, if
   * more, stands.
   */
  private release(c: OrbitInternals): void {
    const k = (1 - DAMPING_COAST) / DAMPING_COAST;
    const v = this.velocity;
    const owe = (now: number, speed: number): number => (Math.abs(speed * k) > Math.abs(now) ? speed * k : now);
    c._sphericalDelta.theta = owe(c._sphericalDelta.theta, v.theta);
    c._sphericalDelta.phi = owe(c._sphericalDelta.phi, v.phi);
    c._panOffset.set(owe(c._panOffset.x, v.pan.x), owe(c._panOffset.y, v.pan.y), owe(c._panOffset.z, v.pan.z));
    this.clearVelocity();
  }

  private clearVelocity(): void {
    this.velocity.theta = 0;
    this.velocity.phi = 0;
    this.velocity.pan.set(0, 0, 0);
  }

  /**
   * Drop whatever the damping still owes the view (the pen took over from
   * fingers mid-orbit): the camera stops where it is, rather than drifting
   * on under the first stroke. The release that follows a takeover must
   * not start a coast either, so the held state and its speed go too.
   */
  halt(): void {
    const c = this.controls as unknown as OrbitInternals;
    c._sphericalDelta.set(0, 0, 0);
    c._panOffset.set(0, 0, 0);
    c._scale = 1;
    this.held = false;
    this.clearVelocity();
  }

  /**
   * Turntable step: rotate the camera around the world Y axis about the
   * orbit target (sculpt wheel keys; positive = model appears to turn
   * right). Position-based, so OrbitControls just re-derives its spherical
   * on the next update.
   */
  rotateAzimuth(deg: number): void {
    const offset = new Vector3().subVectors(this.camera.position, this.controls.target);
    const rad = (deg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const x = offset.x * cos - offset.z * sin;
    const z = offset.x * sin + offset.z * cos;
    offset.x = x;
    offset.z = z;
    this.camera.position.copy(this.controls.target).add(offset);
    this.camera.lookAt(this.controls.target);
    this.controls.update();
  }

  /**
   * Place camera and target without running the controls. setState() ends in
   * controls.update(), which re-applies the frame's damped delta - fine when
   * called once, wrong from inside a post-update hook, where it applies the
   * same easing twice and skews the result.
   */
  placeCamera(position: Vector3, target: Vector3): void {
    this.camera.position.copy(position);
    this.controls.target.copy(target);
    this.camera.lookAt(this.controls.target);
    this.camera.updateMatrixWorld();
  }

  /**
   * Turntable: rotate the camera AND the orbit target about a vertical axis
   * through `centre`. Rotating the target too keeps the view relationship
   * intact, so this stays a true turntable however far the stroke pivot has
   * wandered from the object's middle.
   */
  rotateAzimuthAbout(centre: Vector3, deg: number): void {
    const rad = (deg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const spin = (p: Vector3): void => {
      const dx = p.x - centre.x;
      const dz = p.z - centre.z;
      p.x = centre.x + dx * cos + dz * sin;
      p.z = centre.z - dx * sin + dz * cos;
    };
    spin(this.camera.position);
    spin(this.controls.target);
    this.camera.lookAt(this.controls.target);
    this.controls.update();
  }

  /**
   * Dolly by a multiplier (>1 pulls back, <1 moves in), clamped to the same
   * min/max the wheel and the pinch obey: along the view ray, or with a
   * zoom focus set, towards the point it gives for `at` (see zoomAbout).
   * Drives the sculpt ctrl-drag zoom, which exists so a Pencil can zoom
   * without a pinch gesture. A step is a proportion of the distance, as the
   * wheel's is, so it magnifies by the same amount at any distance; it does
   * not shrink to nothing near the floor, it stops at it.
   */
  dollyBy(factor: number, at: ScreenPoint | null = null): void {
    if (this.zoomFocus) {
      this.zoomAbout(factor, at);
      this.syncLimits();
      this.controls.update();
      return;
    }
    const offset = new Vector3().subVectors(this.camera.position, this.controls.target);
    const dist = offset.length();
    if (dist < 1e-6) return;
    const next = Math.min(
      this.controls.maxDistance,
      Math.max(this.controls.minDistance, dist * factor),
    );
    this.camera.position.copy(this.controls.target).addScaledVector(offset.normalize(), next);
    this.syncLimits();
    this.controls.update();
  }

  /**
   * Where zooms head from now on (Sculpt: the last edit, or the surface
   * under the pointer), or null for OrbitControls' own zoom to the target.
   */
  setZoomFocus(focus: ZoomFocus | null): void {
    this.zoomFocus = focus;
  }

  /**
   * Where the zoom under way is on the page: the middle of the fingers for
   * a pinch, the press for a drag zoom, otherwise the last place the mouse,
   * the pen or the wheel was.
   */
  private zoomAt(): ScreenPoint | null {
    if (this.touches.size >= 2 && this.isHeld()) {
      let x = 0;
      let y = 0;
      for (const p of this.touches.values()) {
        x += p.x;
        y += p.y;
      }
      return { x: x / this.touches.size, y: y / this.touches.size };
    }
    if (this.drag && this.dragAt) return this.dragAt;
    return this.pointerAt;
  }

  /**
   * The zoom OrbitControls owes (its _scale, from the wheel, a pinch or a
   * right drag), taken over when a zoom focus is set: applied about the
   * focus, and nothing left for its own update to dolly by.
   */
  private zoomTowardFocus(): void {
    if (!this.zoomFocus) return;
    const c = this.controls as unknown as OrbitInternals;
    const scale = c._scale;
    if (scale === 1) return;
    c._scale = 1;
    this.zoomAbout(scale, this.zoomAt());
  }

  /**
   * Zoom by `factor` towards the focus point for `at` (the target when
   * there is none): camera and target both scale about that point, so the
   * camera dollies along the ray through it, the view keeps its direction,
   * and the point stays where it is on the screen - the target moves
   * towards it in proportion, as OrbitControls' zoomToCursor moves it
   * towards the cursor. The camera-to-target distance scales by the same
   * factor, so the floor and the ceiling (and the near plane following
   * the distance) hold exactly as for a zoom to the target.
   */
  private zoomAbout(factor: number, at: ScreenPoint | null): void {
    const target = this.controls.target;
    const dist = this.camera.position.distanceTo(target);
    if (dist < 1e-6) return;
    const next = Math.min(this.controls.maxDistance, Math.max(this.controls.minDistance, dist * factor));
    const s = next / dist;
    if (s === 1) return;
    // The camera's matrices as it now stands: picking reads them.
    this.camera.updateMatrixWorld();
    const p = this.zoomScratch.copy(this.zoomFocus?.(at) ?? target);
    this.camera.position.sub(p).multiplyScalar(s).add(p);
    target.sub(p).multiplyScalar(s).add(p);
    this.onFocusZoom?.(p, s);
  }

  /** Distance from the camera to the orbit target (the depth-of-field focus). */
  targetDistance(): number {
    return this.camera.position.distanceTo(this.controls.target);
  }

  /**
   * Dolly along the view ray so the subject keeps its apparent size as the
   * vertical FOV changes: the lens-compression comparison at a fixed framing.
   * Moving back for a longer lens (and in for a wider one) is what a photographer
   * does to keep the subject filling the frame across focal lengths.
   */
  dollyForFov(oldFov: number, newFov: number): void {
    const offset = new Vector3().subVectors(this.camera.position, this.controls.target);
    const dist = offset.length();
    if (dist < 1e-6) return;
    const ratio = Math.tan((oldFov * Math.PI) / 360) / Math.tan((newFov * Math.PI) / 360);
    const newDist = dist * ratio;
    this.camera.position.copy(this.controls.target).addScaledVector(offset.normalize(), newDist);
    // The ceiling grows with the lens (twice its framing distance), so the
    // dolly that keeps the subject's size is not cut short at long lenses.
    this.syncLimits();
    this.controls.update();
  }

  getState(): { position: [number, number, number]; target: [number, number, number] } {
    const p = this.camera.position;
    const t = this.controls.target;
    return { position: [p.x, p.y, p.z], target: [t.x, t.y, t.z] };
  }

  setState(position: number[], target: number[]): void {
    this.camera.position.set(position[0] ?? 0, position[1] ?? 0, position[2] ?? 0);
    this.controls.target.set(target[0] ?? 0, target[1] ?? 0, target[2] ?? 0);
    // The subject's limits, not ones made up from wherever this camera was
    // saved: a session left close in used to come back unable to zoom out
    // past ten times that distance.
    this.syncLimits();
    this.controls.update();
  }

  dispose(): void {
    window.removeEventListener('pointerdown', this.onPressFirst, true);
    this.domElement.removeEventListener('pointerdown', this.onPressAfter);
    window.removeEventListener('pointermove', this.onAnyMove, true);
    window.removeEventListener('pointerup', this.onAnyUp, true);
    window.removeEventListener('pointercancel', this.onAnyCancel, true);
    this.domElement.removeEventListener('lostpointercapture', this.onLostCapture);
    window.removeEventListener('blur', this.onWindowBlur);
    window.removeEventListener('wheel', this.onWheelFirst, { capture: true });
    this.restoreButtons();
    this.controls.dispose();
  }
}
